/**
 * The deployment's own configuration, read back.
 *
 * This is the page an operator reaches when nothing else works, so everything
 * here is a fact about *this* browser's copy of the application rather than
 * anything the backend was asked for.
 */
import {
  backendUrl,
  getRuntimeConfig,
  webFrontendUrl,
  websocketUrl,
  type RuntimeConfig,
} from '@/shared/config/runtime'

/**
 * The environment variable each configuration key comes from.
 *
 * Declared rather than derived. Upstream's debug page turns `cookiePrefix` into
 * `COOKIE_PREFIX` with a camel-case-to-underscore regex, and five of these
 * sixteen variables do not follow that rule -- the one it prints for the cookie
 * prefix is not a variable this image reads at all, which is worse than printing
 * nothing, because it is the name somebody then goes and sets.
 *
 * Exhaustive over `RuntimeConfig`, so a key added to the config without a
 * variable name here fails to compile.
 */
export const ENVIRONMENT_NAMES = {
  publicBackendUrl: 'PUBLIC_BACKEND_URL',
  publicWebFrontendUrl: 'PUBLIC_WEB_FRONTEND_URL',
  mediaUrl: 'MEDIA_URL',
  embeddedShareUrl: 'BASEROW_EMBEDDED_SHARE_URL',
  cookiePrefix: 'BASEROW_FRONTEND_COOKIE_PREFIX',
  sameSiteCookie: 'BASEROW_FRONTEND_SAME_SITE_COOKIE',
  disablePublicUrlCheck: 'BASEROW_DISABLE_PUBLIC_URL_CHECK',
  downloadFileViaXhr: 'DOWNLOAD_FILE_VIA_XHR',
  featureFlags: 'FEATURE_FLAGS',
  sentryDsn: 'SENTRY_DSN',
  sentryEnvironment: 'SENTRY_ENVIRONMENT',
  sentryTracesSampleRate: 'SENTRY_TRACES_SAMPLE_RATE',
  posthogProjectApiKey: 'POSTHOG_PROJECT_API_KEY',
  posthogHost: 'POSTHOG_HOST',
  extraClientScriptUrls: 'BASEROW_EXTRA_CLIENT_SCRIPT_URLS',
  idpIssuer: 'BASEROW_LITHOS_IDP_ISSUER',
} as const satisfies Record<keyof RuntimeConfig, string>

export interface ConfigRow {
  key: keyof RuntimeConfig
  variable: string
  value: string
  /** Whether the deployment gave this one a value at all. */
  isSet: boolean
}

/**
 * One value, as text.
 *
 * A list is joined by newlines rather than rendered as JSON: the variable behind
 * it is a comma-separated string, and a reader is checking whether their entries
 * survived the split, not what the array literal looks like.
 */
export function formatConfigValue(value: unknown): string {
  if (Array.isArray(value)) return value.join('\n')
  return String(value)
}

/**
 * Whether a value counts as configured.
 *
 * `false` and `0` are answers and are reported as such; only an empty string and
 * an empty list mean nothing was said. Calling a `false` unset would tell an
 * operator that `BASEROW_DISABLE_PUBLIC_URL_CHECK=false` had not taken effect.
 */
export function isConfigured(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  return value !== ''
}

export function configRows(): ConfigRow[] {
  const config = getRuntimeConfig()
  return (Object.keys(ENVIRONMENT_NAMES) as (keyof RuntimeConfig)[]).map((key) => ({
    key,
    variable: ENVIRONMENT_NAMES[key],
    value: formatConfigValue(config[key]),
    isSet: isConfigured(config[key]),
  }))
}

export interface OriginRow {
  key: 'backend' | 'webFrontend' | 'websocket'
  url: string
  /**
   * True when nothing was configured and the page's own origin was used. That
   * is correct for a single-domain deployment behind one proxy, and it is the
   * first thing to suspect on any other.
   */
  fromPage: boolean
}

/**
 * Where this page will actually send its requests.
 *
 * The configured value and the address used are different things -- an unset
 * `PUBLIC_BACKEND_URL` silently becomes the page's own origin, and the websocket
 * address is never configured at all -- so the derived answers are reported
 * beside the raw ones rather than left to be worked out.
 *
 * Asked of the runtime module rather than recomputed from the config, because a
 * second copy of the derivation could drift from the one the application uses --
 * on the one page whose whole purpose is to be believed.
 */
export function originRows(): OriginRow[] {
  const config = getRuntimeConfig()
  return [
    {
      key: 'backend',
      url: backendUrl(),
      fromPage: config.publicBackendUrl === '',
    },
    {
      key: 'webFrontend',
      url: webFrontendUrl(),
      fromPage: config.publicWebFrontendUrl === '',
    },
    {
      key: 'websocket',
      url: websocketUrl(),
      fromPage: config.publicBackendUrl === '',
    },
  ]
}
